import {
  Button,
  Caption1,
  DrawerBody,
  mergeClasses,
  OverlayDrawer,
  Tooltip,
} from '@fluentui/react-components';
import {
  bundleIcon,
  ChevronLeftRegular,
  DismissRegular,
  HomeFilled,
  HomeRegular,
} from '@fluentui/react-icons';
import { useLocation, useNavigate } from 'react-router';
import { APP_INFO } from '#src/app/app-info';
import { pagePath } from '#src/app/navigation/module';
import { MODULES } from '#src/app/navigation/modules';
import { findRoute, visibleModules } from '#src/app/navigation/navigation';
import { ModuleGroup, NavButton } from '#src/app/shell/SidebarItems';
import { useSidebarStyles } from '#src/app/shell/sidebar.styles';
import { useAccess } from '#src/auth/session';
import { GoldStars, IdentitySurface } from '#src/theme/IdentitySurface';
import { useThemePreference } from '#src/theme/ThemeProvider';

const HomeIcon = bundleIcon(HomeFilled, HomeRegular);
const HOME = '/';

export interface SidebarProps {
  /** Tela estreita: a navegação é uma gaveta sobre a página, aberta por `open`. */
  readonly overlay: boolean;
  readonly open: boolean;
  onOpenChange(open: boolean): void;
  /** Tela larga: a barra fica sempre visível, expandida ou só com ícones. */
  readonly collapsed: boolean;
  onCollapsedChange(collapsed: boolean): void;
}

function Brand({
  iconsOnly,
  onClick,
}: {
  iconsOnly: boolean;
  onClick: () => void;
}) {
  const styles = useSidebarStyles();
  return (
    <Tooltip
      content="Ir para o início"
      relationship="label"
      positioning={iconsOnly ? 'after' : 'below'}
    >
      <button type="button" className={styles.brand} onClick={onClick}>
        <img className={styles.brandLogo} src={APP_INFO.logo} alt="" />
        {!iconsOnly && (
          <span className={styles.brandText}>
            <span className={styles.brandName}>{APP_INFO.name}</span>
            <Caption1 className={styles.subtle}>{APP_INFO.tagline}</Caption1>
          </span>
        )}
      </button>
    </Tooltip>
  );
}

function SidebarContent({ overlay, onOpenChange, collapsed }: SidebarProps) {
  const styles = useSidebarStyles();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const visible = visibleModules(MODULES, useAccess());
  // Na gaveta a barra está sempre expandida.
  const iconsOnly = collapsed && !overlay;
  // No alto contraste, sem o degradê nem as estrelas da identidade.
  const identity = useThemePreference().mode !== 'contrast';
  const route = findRoute(MODULES, pathname);
  const selected =
    route?.page === undefined ? pathname : pagePath(route.module, route.page);
  const ordered = [
    ...visible.filter(({ module }) => module.placement === 'main'),
    ...visible.filter(({ module }) => module.placement === 'footer'),
  ];

  const go = (path: string) => {
    navigate(path);
    if (overlay) {
      onOpenChange(false);
    }
  };

  return (
    <nav
      className={mergeClasses(
        styles.root,
        identity && styles.rootIdentity,
        iconsOnly && styles.rootCollapsed,
        overlay && styles.rootDrawer,
      )}
      aria-label="Navegação principal"
    >
      <div className={styles.header}>
        <Brand iconsOnly={iconsOnly} onClick={() => go(HOME)} />
        {overlay && (
          <Tooltip content="Fechar navegação" relationship="label">
            <Button
              className={styles.close}
              appearance="subtle"
              icon={<DismissRegular />}
              onClick={() => onOpenChange(false)}
            />
          </Tooltip>
        )}
      </div>
      <div className={styles.scroll}>
        <NavButton
          icon={HomeIcon}
          label="Início"
          selected={pathname === HOME}
          collapsed={iconsOnly}
          onClick={() => go(HOME)}
        />
        {ordered.length > 0 && (
          <div className={styles.section}>
            {iconsOnly ? (
              <span className={styles.sectionDash} />
            ) : (
              <Caption1>Módulos</Caption1>
            )}
          </div>
        )}
        {ordered.map((item) => (
          <ModuleGroup
            key={item.module.path}
            {...item}
            selected={selected}
            active={route?.module.path === item.module.path}
            collapsed={iconsOnly}
            onNavigate={go}
          />
        ))}
      </div>
      {identity && !iconsOnly && <GoldStars className={styles.stars} />}
    </nav>
  );
}

/** Navegação lateral: gaveta na tela estreita, barra recolhível na larga. */
export function Sidebar(props: SidebarProps) {
  const styles = useSidebarStyles();
  const { overlay, open, onOpenChange, collapsed, onCollapsedChange } = props;

  if (overlay) {
    return (
      <IdentitySurface kind="sidebar">
        <OverlayDrawer
          open={open}
          onOpenChange={(_, data) => onOpenChange(data.open)}
        >
          <DrawerBody className={styles.drawerBody}>
            <SidebarContent {...props} />
          </DrawerBody>
        </OverlayDrawer>
      </IdentitySurface>
    );
  }
  return (
    <IdentitySurface kind="sidebar" className={styles.wrapper}>
      <SidebarContent {...props} />
      <Tooltip
        content={collapsed ? 'Expandir navegação' : 'Recolher navegação'}
        relationship="label"
        positioning="after"
      >
        <button
          type="button"
          className={styles.handle}
          aria-expanded={!collapsed}
          onClick={() => onCollapsedChange(!collapsed)}
        >
          <ChevronLeftRegular
            className={mergeClasses(
              styles.arrow,
              collapsed && styles.arrowCollapsed,
            )}
          />
        </button>
      </Tooltip>
    </IdentitySurface>
  );
}
